import { useSuspenseQuery } from '@tanstack/react-query';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { meQuery } from '../api';
import { ManageTabs } from '../manage/ManageTabs';
import { managePageAt } from '../manage/pages';
import { Hero, LevelTwoTabs, OpsSidebar, PageBody, type HeroProps } from '../shell';
import { Button, EmptyState } from '../ui';
import { settingsAreasFor, settingsNavKind, type SettingsArea, type SettingsAreaId } from './nav';
import s from './Settings.module.css';

/**
 * Where a settings screen sits: under Settings (everyone) or in Manage's Executive group (X4, X5;
 * APP-236).
 */
export type Section = 'settings' | 'executive';

/**
 * The page around a settings screen: its hero, then Level 2. Under Settings that's a sidebar for
 * owners with many areas, tabs for one or two, nothing for My preferences alone (NAV0's decision);
 * in Manage's Executive group it's Manage's two rows under the hero (APP-236).
 */
export function SettingsFrame({
  section,
  hero,
  children,
}: {
  section: Section;
  hero: HeroProps;
  children: ReactNode;
}): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);
  if (section === 'executive') {
    return (
      <>
        <Hero {...hero} />
        <ManageTabs current={managePageAt(pathname)} />
        <PageBody>{children}</PageBody>
      </>
    );
  }
  const areas = settingsAreasFor(me);
  const kind = settingsNavKind(areas.length);
  // My feedback's badge: the team's replies the person hasn't read yet (APP-138 §D8).
  const newReplies = me.features?.feedback.mine?.newReplies ?? 0;
  const item = (a: SettingsArea) => ({
    id: a.id,
    label: a.label,
    href: a.href,
    // My preferences is current on its own page only, not on My feedback under it.
    end: a.id === 'system' || a.id === 'me',
    ...(a.id === 'my-feedback' && newReplies > 0 && { count: newReplies, countName: 'new' }),
  });
  if (kind === 'sidebar') {
    const groups = (['My preferences', 'Award setup', 'System administration'] as const)
      .map((label) => ({
        label,
        items: areas.filter((a) => a.group === label).map(item),
      }))
      .filter((g) => g.items.length > 0);
    return (
      <>
        <Hero {...hero} />
        <PageBody>
          <div className={s.sidebarHost}>
            <div className={s.withSidebar}>
              <div className={s.navigation} data-expanded={expanded}>
                <div className={s.mobileMenu}>
                  <Button
                    variant="ghost"
                    aria-expanded={expanded}
                    aria-controls="settings-sections"
                    onPress={() => {
                      setExpanded(!expanded);
                    }}
                  >
                    Settings sections
                  </Button>
                </div>
                <div id="settings-sections" className={s.sectionLinks}>
                  <OpsSidebar label="Settings sections" groups={groups} className={s.sidebar} />
                </div>
              </div>
              <div className={s.sideMain}>{children}</div>
            </div>
          </div>
        </PageBody>
      </>
    );
  }
  return (
    <>
      <Hero {...hero} />
      {kind === 'tabs' && <LevelTwoTabs label="Settings sections" items={areas.map(item)} />}
      <PageBody>{children}</PageBody>
    </>
  );
}

/** A settings area this person's roles don't include (they followed an old link). */
export function NotYourArea({ area }: { area: SettingsAreaId | 'executive' }): ReactElement {
  return (
    <>
      <Hero variant="slim" title="Settings" />
      <PageBody>
        <EmptyState
          variant="first-day"
          art="shield"
          headingLevel={2}
          headline="This part of Settings isn’t for your roles"
          body={
            area === 'executive'
              ? 'Budget pools and values are kept by the Executives. Your own settings are in My preferences.'
              : 'Each person sees the settings their roles look after. Your own settings are in My preferences.'
          }
          action={{ label: 'Go to My preferences', href: '/settings/me' }}
        />
      </PageBody>
    </>
  );
}
